'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DisGicirdatmaBruksizmTedavisindeBotoksPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dis-gicirdatma-bruksizm-tedavisinde-botoks';
  const pageTitle = 'Diş Gıcırdatma (Bruksizm) Tedavisinde Botoks';
  const pageDescription = 'Diş gıcırdatma (bruksizm) nedir, neden olur? Botoks tedavisiyle çene kaslarını gevşetmek, ağrıyı ve diş aşınmasını azaltmak mümkün mü?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Diş Gıcırdatma Tedavisinde Botoks', href: '/tr/blog/dis-gicirdatma-bruksizm-tedavisinde-botoks' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Diş gıcırdatma bruksizm tedavisinde botoks uygulaması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Fonksiyonel Tedavi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Diş Gıcırdatma (Bruksizm) Tedavisinde Botoks
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Diş sıkma veya gıcırdatma, tıbbi adıyla bruksizm, modern çağın en sık görülen stres kaynaklı alışkanlıklarından biridir. Uykuda ya da gün içinde farkında olmadan dişleri sıkmak, hem diş minesine zarar verir, hem de çene eklemi ve kaslarında ağrıya yol açar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Klasik tedavi seçenekleri arasında gece plağı, stres yönetimi ve fizik tedavi yer alır. Ancak son yıllarda botoks, bu sorunun hem etkin hem konforlu çözümü olarak öne çıkmıştır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Bruksizm Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bruksizm, bireyin istemsiz şekilde dişlerini sıkması veya gıcırdatmasıyla karakterize bir durumdur. Genellikle stres, anksiyete, uyku bozukluğu veya kas hiperaktivitesi sonucu ortaya çıkar.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bruksizm iki ana şekilde görülür:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li><strong>Uyku bruksizmi:</strong> Uykuda farkında olmadan diş sıkma veya gıcırdatma</li>
                  <li><strong>Gündüz bruksizmi:</strong> Günlük stres, öfke veya gerginlik sırasında bilinçli sıkma</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <p className="text-yellow-800 font-medium">
                  Eğer sabah çene ağrısı, baş ağrısı veya diş aşınmasıyla uyanıyorsanız, bu belirtiler bruksizme işaret ediyor olabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Bruksizm Tedavisinde Botoks Nasıl Etki Eder?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum toksin, masseter ve bazen temporalis kasına enjekte edilir. Bu kasların aşırı kasılmasını sağlayan asetilkolin geçici olarak bloke edilir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Sonuç:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kasın gücü azalır</li>
                  <li>Çene sıkma refleksi kırılır</li>
                  <li>Ağrı, gerginlik ve diş aşınması azalır</li>
                </ul>
                <div className="mt-4 space-y-2">
                  <p className="text-green-800">💎 Etki süresi: 3–5 gün içinde başlar</p>
                  <p className="text-green-800">🕒 Tam etki: 10–14 gün</p>
                  <p className="text-green-800">📆 Kalıcılık süresi: Ortalama 4–6 ay</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Bruksizmde Botoksun Faydaları
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Alanı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Sonuç
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kas aktivitesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">%70–90 azalma</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Masseter kas gevşer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Diş koruması</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Diş minesinde aşınma durur</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Gece plağına gerek azalır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Çene eklemi ağrısı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Belirgin azalma</td>
                      <td className="px-6 py-4 text-sm text-gray-900">TME üzerindeki baskı düşer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Estetik görünüm</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüz incelmesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Masseter kas hacmi küçülür</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Uyku kalitesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Artış</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Rahatlama ve gevşeme sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium">
                  💡 Çifte etki: Hem fonksiyonel iyileşme hem estetik incelme sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Uygulama Süreci
              </h2>
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-indigo-800 space-y-2">
                  <li>İşlem öncesi kas aktivitesi değerlendirilir.</li>
                  <li>Lokal anestezik krem uygulanabilir (gerektiğinde).</li>
                  <li>Masseter kasına 3–5 noktadan mikro enjeksiyon yapılır.</li>
                  <li>İşlem süresi 10–15 dakika olup, sonrasında istirahat gerekmez.</li>
                </ol>
                <p className="text-indigo-800 mt-3 font-medium">
                  💬 Kişi hemen normal hayatına dönebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Botoksun Etkisi Ne Kadar Sürer?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Etkisi 4–6 ay sürer; düzenli uygulamalarda 9 aya kadar uzar. Kas hafızası zayıfladıkça diş sıkma refleksi azalır. Bazı hastalarda 3. seans sonrası kalıcı gevşeme sağlanabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Kimler İçin Uygundur?
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium mb-3">✅ Uygundur:</p>
                <ul className="list-disc list-inside text-green-800 space-y-1">
                  <li>Bruksizm tanısı konulmuş hastalar</li>
                  <li>Sabah çene ağrısı, baş ağrısı yaşayanlar</li>
                  <li>Gece plağı kullanmakta zorlananlar</li>
                  <li>Kare yüz hattına sahip, çene kası belirgin kişiler</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 font-medium mb-3">⚠️ Uygulanmaz:</p>
                <ul className="list-disc list-inside text-red-800 space-y-1">
                  <li>Gebelik ve emzirme döneminde</li>
                  <li>Nöromüsküler hastalık öyküsü olanlarda</li>
                  <li>Enjeksiyon bölgesinde enfeksiyon varsa</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İlk 4 saat eğilmemek, masaj yapmamak</li>
                  <li>İlk 24 saat spor, sauna, sıcak duş yapmamak</li>
                  <li>48 saat aşırı çiğneme hareketlerinden kaçınmak</li>
                  <li>Bol su içmek, kafein ve stres faktörlerinden uzak durmak</li>
                </ul>
                <p className="text-orange-800 mt-3 font-medium">
                  💡 Etkiyi uzatmak için gece diş sıkmayı tetikleyen alışkanlıklardan (kahve, stres, uykusuzluk) kaçınmak önemlidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Botoksun Yan Etkileri ve Güvenlik
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Doğru teknik ve dozda uygulandığında son derece güvenlidir. Nadir görülen geçici yan etkiler:
              </p>

              <div className="bg-teal-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-teal-800 space-y-2">
                  <li>Hafif çiğneme zayıflığı (1–2 hafta içinde düzelir)</li>
                  <li>Geçici asimetri</li>
                  <li>Enjeksiyon yerinde kızarıklık</li>
                </ul>
                <p className="text-teal-800 mt-3 font-medium">
                  Ciddi komplikasyon riski %1'in altındadır.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Diş gıcırdatma bruksizm tedavisinde botoks uygulaması"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoks diş sıkmayı tamamen durdurur mu?</h3>
                  <p className="text-gray-700">Kasın gücünü azaltarak diş sıkmayı büyük oranda engeller, genelde %80–90 başarı oranı vardır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi geçince diş sıkma geri döner mi?</h3>
                  <p className="text-gray-700">Zamanla hafifleyerek geri dönebilir; düzenli uygulamalarda kalıcılık artar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoks sonrası yüzüm incelir mi?</h3>
                  <p className="text-gray-700">Evet, masseter kası küçüldükçe yüz kontürü daha oval hale gelir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Botoks ağrılı mıdır?</h3>
                  <p className="text-gray-700">Hayır. İnce iğnelerle yapılır, işlem birkaç dakika sürer ve konforludur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Gece plağı ile birlikte uygulanabilir mi?</h3>
                  <p className="text-gray-700">Evet. Ağır bruksizm vakalarında kombine yaklaşım en etkilidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Oral and Maxillofacial Surgeons (AAOMS) – "Botulinum Toxin for Bruxism"</li>
                <li>Türk Diş Hekimleri Birliği (TDB) – "Bruksizm Tedavi Kılavuzu (2024)"</li>
                <li>Harvard Health Publishing (2023) – "Treating Teeth Grinding with Botox"</li>
                <li>Mayo Clinic (2024) – "Non-surgical Management of TMJ and Bruxism"</li>
                <li>ISAPS Global Report 2024 – "Functional and Aesthetic Outcomes of Masseter Botox"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#bruksizm</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#dişsıkma</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#çenebotoksu</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#masseterbotoksu</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#tmetedavisi</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#plağsızçözüm</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Bruksizm Tedavisi Hakkında Bilgi Alın</h3>
              <p className="mb-6">Diş gıcırdatma ve çene sıkma sorunlarınız için botoks tedavisi hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Bruksizm tedavisinde botoks hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-green-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/ceneye-botoks-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Çeneye Botoks Uygulaması Nedir?</h4>
              <p className="text-gray-600 text-sm">Çeneye botoks uygulaması nasıl yapılır, kimlere uygundur ve etkisi ne kadar sürer?</p>
            </Link>
            <Link
              href="/tr/blog/dis-hekimlerinin-botoks-yapma-yetkisi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Diş Hekimlerinin Botoks Yapma Yetkisi</h4>
              <p className="text-gray-600 text-sm">Diş hekimleri botoks uygulayabilir mi? Yasal düzenlemeler ve yetki alanları.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
